/**
 * Board Item Edit Form
 * 
 * A reusable form for editing categories, utterances, or navigation items.
 * Supports editing name/text, emoji/icon, color, and whether to show emoji.
 * Can switch between emoji mode and Lucide icon mode via a toggle.
 */

import React, { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { Check, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { MainLayout } from "@/components/layout/MainLayout";
import { EmojiPicker } from "@/components/ui/EmojiPicker";
import { LucideIconPicker } from "@/components/ui/LucideIconPicker";
import { ColorPicker, BoardColorId } from "@/components/communication/ColorPicker";
import { NavigationColorPicker, NavigationColorId } from "@/components/communication/NavigationColorPicker";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type IconMode = "emoji" | "lucide";

interface BoardItemEditFormProps {
  type: "category" | "utterance" | "navigation";
  initialName: string;
  initialEmoji: string;
  initialColor: BoardColorId;
  initialShowEmoji?: boolean;
  initialIconName?: string; // For Lucide icons
  iconMode?: IconMode; // Initial picker mode
  hideColorPicker?: boolean; // Hide color picker (for group-color editing)
  hideVisualElement?: boolean; // Hide entire visual element section (text+color only)
  iconOnly?: boolean; // When true, disable emoji/upload tabs (only icon picker available)
  emojiOnly?: boolean; // When true, disable icon tab (only emoji picker available)
  onSave: (data: { 
    name: string; 
    emoji: string; 
    color: BoardColorId; 
    showEmoji: boolean;
    iconName?: string;
    iconMode?: IconMode;
  }) => void;
  onCancel: () => void;
  isNew?: boolean;
}

export function BoardItemEditForm({
  type,
  initialName,
  initialEmoji,
  initialColor,
  initialShowEmoji = true,
  initialIconName = "Home",
  iconMode: initialIconMode = "emoji",
  hideColorPicker = false,
  hideVisualElement = false,
  iconOnly = false,
  emojiOnly = false,
  onSave,
  onCancel,
  isNew = false,
}: BoardItemEditFormProps) {
  const { t, dir, isRTL } = useCaregiverTranslation();
  const [name, setName] = useState(initialName);
  const [emoji, setEmoji] = useState(initialEmoji);
  const [iconName, setIconName] = useState(initialIconName);
  const [color, setColor] = useState<BoardColorId>(initialColor);
  const [showEmoji, setShowEmoji] = useState(initialShowEmoji);
  const [currentIconMode, setCurrentIconMode] = useState<IconMode>(initialIconMode);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setEmoji(base64);
        setCurrentIconMode("emoji"); // Switch to emoji mode when uploading custom image
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ 
      name: name.trim(), 
      emoji: currentIconMode === "emoji" ? emoji : "", 
      color, 
      showEmoji,
      iconName: currentIconMode === "lucide" ? iconName : undefined,
      iconMode: currentIconMode,
    });
  };

  const getTitle = () => {
    if (type === "navigation") {
      return isRTL ? "עריכת פריט" : "Edit Item";
    }
    if (isNew) {
      return type === "category"
        ? (isRTL ? "הוספת קטגוריה" : "Add Category")
        : (isRTL ? "הוסף כרטיס" : "Add Card");
    }
    return type === "category"
      ? (isRTL ? "עריכת קטגוריה" : "Edit Category")
      : (isRTL ? "עריכת כרטיס" : "Edit Card");
  };

  const getNameLabel = () => {
    if (type === "navigation") {
      return isRTL ? "שם" : "Name";
    }
    return type === "category" 
      ? (isRTL ? "שם" : "Name") 
      : (isRTL ? "טקסט" : "Text");
  };

  const namePlaceholder = type === "category" || type === "navigation"
    ? (isRTL ? "שם הפריט" : "Item name")
    : (isRTL ? "טקסט הכרטיס" : "Card text");

  return (
    <MainLayout onBack={onCancel}>
      <div className="flex-1 flex flex-col p-6 overflow-auto bg-white dark:bg-transparent" dir={dir}>
        <h1 className="text-2xl font-bold text-foreground mb-6">{getTitle()}</h1>

        <div className="max-w-lg mx-auto w-full space-y-6">
          {/* Name/Text Input */}
          <div>
            <label className="block text-lg font-medium mb-2">
              {getNameLabel()}
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={namePlaceholder}
              className="h-14 text-lg"
              dir={dir}
            />
          </div>

          {/* Visual Element Section - hidden when hideVisualElement is true */}
          {!hideVisualElement && (
          <div className="space-y-3">
            <label className="block text-lg font-medium">
              {isRTL ? "אלמנט ויזואלי" : "Visual Element"}
            </label>
            
            {/* Show/Hide Visual Element Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl opacity-50">
                    <label className="text-base text-muted-foreground">
                      {isRTL ? "הצג אלמנט ויזואלי" : "Show Visual Element"}
                    </label>
                    <Switch
                      checked={showEmoji}
                      disabled
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isRTL ? "אפשרות זו אינה זמינה כרגע" : "This option is not available yet"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Visual Element Picker - only show if showEmoji is enabled */}
            {showEmoji && (() => {
              const hasCustomImage = emoji.startsWith("data:image");
              return (
              <div className="space-y-4">
                {/* Hidden file input for custom image upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />

                {/* Custom image preview if uploaded */}
                {hasCustomImage && (
                  <div className="flex items-center justify-center p-4 bg-muted rounded-xl">
                    <img src={emoji} alt="Custom" className="w-16 h-16 object-contain rounded-lg" />
                  </div>
                )}

                {/* Upload Image button - always visible above tabs */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          onClick={iconOnly ? undefined : triggerFileUpload}
                          disabled={iconOnly}
                          className={cn("gap-2", iconOnly && "opacity-40 cursor-not-allowed")}
                        >
                          <Upload className="w-5 h-5" />
                          {isRTL ? "העלאת תמונה" : "Upload Image"}
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {iconOnly && (
                      <TooltipContent>
                        <p>{isRTL ? "לא זמין בלוח זה" : "Not available for this board"}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                {/* Type selector: Emoji / Icon */}
                <div className="flex items-center justify-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex-1">
                          <button
                            onClick={() => { if (!iconOnly) { if (hasCustomImage) setEmoji(""); setCurrentIconMode("emoji"); } }}
                            disabled={iconOnly}
                            className={cn(
                              "w-full py-2 px-3 rounded-md text-sm font-medium transition-all",
                              iconOnly
                                ? "opacity-40 cursor-not-allowed bg-background"
                                : !hasCustomImage && currentIconMode === "emoji"
                                  ? "bg-primary text-primary-foreground shadow-sm"
                                  : "bg-background hover:bg-accent"
                            )}
                          >
                            {isRTL ? "אימוג'י" : "Emoji"}
                          </button>
                        </span>
                      </TooltipTrigger>
                      {iconOnly && (
                        <TooltipContent>
                          <p>{isRTL ? "לא זמין בלוח זה" : "Not available for this board"}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex-1">
                          <button
                            onClick={() => { if (!emojiOnly) { if (hasCustomImage) setEmoji(""); setCurrentIconMode("lucide"); } }}
                            disabled={emojiOnly}
                            className={cn(
                              "w-full py-2 px-3 rounded-md text-sm font-medium transition-all",
                              emojiOnly
                                ? "opacity-40 cursor-not-allowed bg-background"
                                : !hasCustomImage && currentIconMode === "lucide"
                                  ? "bg-primary text-primary-foreground shadow-sm"
                                  : "bg-background hover:bg-accent"
                            )}
                          >
                            {isRTL ? "אייקון" : "Icon"}
                          </button>
                        </span>
                      </TooltipTrigger>
                      {emojiOnly && (
                        <TooltipContent>
                          <p>{isRTL ? "לא זמין בלוח זה" : "Not available for this board"}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Picker - hidden when custom image is active */}
                {!hasCustomImage && (
                  currentIconMode === "lucide" ? (
                    <LucideIconPicker
                      selectedIcon={iconName}
                      onIconSelect={setIconName}
                      isHebrew={isRTL}
                    />
                  ) : (
                    <EmojiPicker
                      selectedEmoji={emoji}
                      onEmojiSelect={setEmoji}
                      isHebrew={isRTL}
                    />
                  )
                )}
              </div>
              );
            })()}
          </div>
          )}

          {/* Color Picker - conditionally hidden */}
          {!hideColorPicker && (
            type === "navigation" ? (
              <div>
                <label className="block text-lg font-medium mb-2">
                  {isRTL ? "צבע" : "Color"}
                </label>
                <NavigationColorPicker
                  selectedColor={color}
                  onSelect={(c) => setColor(c as BoardColorId)}
                />
              </div>
            ) : (
              <div>
                <label className="block text-lg font-medium mb-2">
                  {isRTL ? "צבע" : "Color"}
                </label>
                <ColorPicker
                  selectedColor={color}
                  onSelect={setColor}
                />
              </div>
            )
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6">
            <Button onClick={handleSave} className="flex-1 h-16 text-xl" disabled={!name.trim()}>
              <Check className="w-6 h-6 mr-2" />
              {t("save")}
            </Button>
            <Button variant="outline" onClick={onCancel} className="h-16 px-8 text-lg">
              {t("cancel")}
            </Button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}